import {
  Activity,
  AlertTriangle,
  Bell,
  Bot,
  Check,
  CheckCheck,
  CheckCircle2,
  ExternalLink,
  FileText,
  GitBranch,
  ListTodo,
  Loader2,
  LogOut,
  Menu,
  MessageSquare,
  MessagesSquare,
  Presentation,
  Telescope,
  Trash2,
  User as UserIcon,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getNotificationRoute, NOTIFICATION_TYPE_COLORS, timeAgo } from "@/constants/notifications";
import type { CompletedOperation } from "@/context";
import { useOperations } from "@/context";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import type { ActiveOperation } from "@/lib/services";

// ── Operation type metadata ─────────────────────────────────────────────────

const OPERATION_TYPE_META: Record<string, { label: string; icon: React.ElementType; basePath: string }> = {
  // Chat variants
  chat: { label: "Chat", icon: MessageSquare, basePath: "/chat" },
  agent_chat: { label: "Chat", icon: MessageSquare, basePath: "/chat" },
  // Group chat
  group_chat: { label: "Brainstorm", icon: MessagesSquare, basePath: "/group-chat" },
  // Tasks
  task: { label: "Autopilot", icon: ListTodo, basePath: "/tasks" },
  task_execution: { label: "Autopilot", icon: ListTodo, basePath: "/tasks" },
  // Deep Research
  deep_research: { label: "Deep Research", icon: Telescope, basePath: "/deep-research" },
  // Flows
  flow: { label: "Workflow", icon: GitBranch, basePath: "/flows" },
  flow_run: { label: "Workflow", icon: GitBranch, basePath: "/flows" },
  // SoWift
  sowift: { label: "SoWift", icon: FileText, basePath: "/apps/sowift" },
  sowift_interview: { label: "SoWift", icon: FileText, basePath: "/apps/sowift" },
  // Presentations
  presentation: { label: "Presentation", icon: Presentation, basePath: "/apps/presentations" },
  presentation_outline: { label: "Presentation", icon: Presentation, basePath: "/apps/presentations" },
  presentation_slides: { label: "Presentation", icon: Presentation, basePath: "/apps/presentations" },
  presentation_refine: { label: "Presentation", icon: Presentation, basePath: "/apps/presentations" },
  // Agents (entity_id is always a chat session UUID, so navigate to /chat)
  agent: { label: "Agent", icon: Bot, basePath: "/chat" },
};

/** Build a URL to navigate to an operation's entity page. */
function getOperationUrl(op: ActiveOperation | CompletedOperation): string {
  const meta = OPERATION_TYPE_META[op.operation_type];
  if (!meta || !op.entity_id) return "#";
  return `${meta.basePath}/${op.entity_id}`;
}

/** Threshold (ms) after which an operation is shown with a staleness hint. */
const STALE_HINT_MS = 5 * 60 * 1000; // 5 minutes

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: Readonly<HeaderProps>) {
  const { profile, logout } = useAuth();
  const {
    notifications,
    unreadCount,
    isLoading: isLoadingNotifications,
    markRead,
    markAllRead,
    deleteNotification,
  } = useNotifications();
  const { activeOperations, recentlyCompleted, operationCount } = useOperations();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showOperations, setShowOperations] = useState(false);

  // Pre-compute staleness for each operation
  const operationsWithStaleness = useMemo(() => {
    const now = Date.now();
    return activeOperations.map((op) => {
      const updatedAt = op.updated_at ? new Date(op.updated_at).getTime() : 0;
      const isStale = updatedAt > 0 && now - updatedAt > STALE_HINT_MS;
      return { ...op, isStale };
    });
  }, [activeOperations]);

  const user = profile?.user;
  if (!user) return null;

  return (
    <header className="h-16 bg-surface-primary border-b border-border-primary flex items-center justify-between px-6">
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 text-content-tertiary hover:text-content-primary hover:bg-surface-secondary rounded-lg transition"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      <div className="flex items-center gap-4">
        {/* Operations indicator — always visible */}
        <div className="relative">
          <button
            onClick={() => setShowOperations(!showOperations)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition cursor-pointer ${
              operationCount > 0 ? "bg-brand-primary-subtle hover:bg-brand-primary/20" : "hover:bg-surface-secondary"
            }`}
          >
            <Activity
              className={`w-3.5 h-3.5 ${
                operationCount > 0 ? "text-brand-primary animate-pulse" : "text-content-tertiary"
              }`}
            />
            {operationCount > 0 && (
              <span className="text-xs font-medium text-brand-primary">{operationCount} running</span>
            )}
          </button>

          {showOperations && (
            <>
              <div
                className="fixed inset-0 z-10"
                role="presentation" // NOSONAR — decorative backdrop element
                onClick={() => setShowOperations(false)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowOperations(false);
                }}
              />
              <div className="absolute top-full right-0 mt-2 w-80 bg-surface-elevated border border-border-secondary rounded-lg shadow-xl z-20 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
                  <h3 className="text-sm font-semibold text-content-primary">Operations</h3>
                  {operationCount > 0 && (
                    <span className="text-xs text-brand-primary font-medium">{operationCount} running</span>
                  )}
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {/* Active operations */}
                  {operationsWithStaleness.length > 0 &&
                    operationsWithStaleness.map((op) => {
                      const meta = OPERATION_TYPE_META[op.operation_type];
                      const Icon = meta?.icon || Activity;
                      const label = meta?.label || op.operation_type;
                      const url = getOperationUrl(op);

                      return (
                        <button
                          key={op.operation_id}
                          onClick={() => {
                            if (url !== "#") {
                              navigate(url);
                            }
                            setShowOperations(false);
                          }}
                          className="w-full flex items-start gap-3 px-4 py-3 hover:bg-interactive-hover transition text-left group"
                        >
                          {/* Icon */}
                          <div
                            className={`mt-0.5 shrink-0 p-1.5 rounded-md ${
                              op.isStale
                                ? "bg-yellow-500/10 text-yellow-500"
                                : "bg-brand-primary-subtle text-brand-primary"
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-content-tertiary">{label}</span>
                              {op.isStale && (
                                <span
                                  className="flex items-center gap-0.5 text-[10px] text-yellow-500"
                                  title="This operation may be stuck — no activity for 5+ minutes"
                                >
                                  <AlertTriangle className="w-2.5 h-2.5" />
                                  May be stale
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-content-primary truncate mt-0.5">
                              {op.title || "Untitled operation"}
                            </p>
                            <span className="text-[10px] text-content-quaternary">
                              Started {timeAgo(op.started_at)}
                            </span>
                          </div>

                          {/* Navigate arrow */}
                          {url !== "#" && (
                            <div className="shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition">
                              <ExternalLink className="w-3.5 h-3.5 text-content-quaternary" />
                            </div>
                          )}
                        </button>
                      );
                    })}

                  {/* Recently completed */}
                  {recentlyCompleted.length > 0 && (
                    <>
                      {operationsWithStaleness.length > 0 && <div className="border-t border-border-secondary" />}
                      <div className="px-4 py-2">
                        <span className="text-[10px] font-semibold text-content-quaternary uppercase tracking-wider">
                          Recently finished
                        </span>
                      </div>
                      {recentlyCompleted.map((op) => {
                        const meta = OPERATION_TYPE_META[op.operation_type];
                        const Icon = meta?.icon || Activity;
                        const label = meta?.label || op.operation_type;
                        const url = getOperationUrl(op);
                        const isSuccess = op.status === "completed";

                        return (
                          <button
                            key={op.operation_id}
                            onClick={() => {
                              if (url !== "#") {
                                navigate(url);
                              }
                              setShowOperations(false);
                            }}
                            className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-interactive-hover transition text-left group"
                          >
                            {/* Icon */}
                            <div
                              className={`mt-0.5 shrink-0 p-1.5 rounded-md ${
                                isSuccess ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"
                              }`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-content-tertiary">{label}</span>
                                <span
                                  className={`flex items-center gap-0.5 text-[10px] ${
                                    isSuccess ? "text-emerald-500" : "text-red-400"
                                  }`}
                                >
                                  {isSuccess ? (
                                    <>
                                      <CheckCircle2 className="w-2.5 h-2.5" /> Completed
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-2.5 h-2.5" /> Failed
                                    </>
                                  )}
                                </span>
                              </div>
                              <p className="text-sm text-content-secondary truncate mt-0.5">
                                {op.title || "Untitled operation"}
                              </p>
                              <span className="text-[10px] text-content-quaternary">
                                Finished {timeAgo(op.completed_at)}
                              </span>
                            </div>

                            {/* Navigate arrow */}
                            {url !== "#" && (
                              <div className="shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition">
                                <ExternalLink className="w-3.5 h-3.5 text-content-quaternary" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </>
                  )}

                  {/* Empty state */}
                  {operationsWithStaleness.length === 0 && recentlyCompleted.length === 0 && (
                    <div className="py-8 text-center">
                      <Activity className="w-6 h-6 text-content-quaternary mx-auto mb-2" />
                      <p className="text-sm text-content-tertiary">No recent operations</p>
                      <p className="text-xs text-content-quaternary mt-1">
                        Operations appear here when you start a task, chat, or other AI action.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-content-tertiary hover:text-content-primary hover:bg-surface-secondary rounded-lg transition"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-pink-500 rounded-full">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <>
              <div
                className="fixed inset-0 z-10"
                role="presentation" // NOSONAR — decorative backdrop element
                onClick={() => setShowNotifications(false)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowNotifications(false);
                }}
              />
              <div className="absolute top-full right-0 mt-2 w-96 bg-surface-elevated border border-border-secondary rounded-lg shadow-xl z-20 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
                  <h3 className="text-sm font-semibold text-content-primary">Notifications</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={() => markAllRead()}
                        className="text-xs text-content-tertiary hover:text-content-primary flex items-center gap-1 transition"
                      >
                        <CheckCheck className="w-3.5 h-3.5" />
                        Mark all read
                      </button>
                    )}
                  </div>
                </div>

                {/* List */}
                <div className="max-h-96 overflow-y-auto">
                  {(() => {
                    if (isLoadingNotifications) {
                      return (
                        <div className="py-12 text-center">
                          <Loader2 className="w-6 h-6 text-content-tertiary animate-spin mx-auto" />
                        </div>
                      );
                    }
                    if (notifications.length === 0) {
                      return (
                        <div className="py-12 text-center">
                          <Bell className="w-8 h-8 text-content-quaternary mx-auto mb-2" />
                          <p className="text-sm text-content-tertiary">No notifications yet</p>
                        </div>
                      );
                    }
                    return notifications.map((notif) => {
                      const route = getNotificationRoute(notif);
                      return (
                        <button
                          type="button"
                          key={notif.uuid}
                          className={`group w-full flex items-start gap-3 px-4 py-3 hover:bg-interactive-hover transition cursor-pointer text-left ${
                            notif.readAt ? "" : "bg-surface-secondary/80"
                          }`}
                          onClick={() => {
                            if (!notif.readAt) markRead(notif.uuid);
                            if (route) {
                              navigate(route);
                              setShowNotifications(false);
                            }
                          }}
                        >
                          {/* Unread dot */}
                          <div className="mt-1.5 shrink-0">
                            {notif.readAt ? (
                              <div className="w-2 h-2" />
                            ) : (
                              <div className="w-2 h-2 rounded-full bg-brand-primary" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span
                                className={`text-xs font-medium ${
                                  NOTIFICATION_TYPE_COLORS[notif.type] || "text-content-tertiary"
                                }`}
                              >
                                {notif.title}
                              </span>
                              <span className="text-[10px] text-content-quaternary shrink-0">
                                {timeAgo(notif.createdAt)}
                              </span>
                            </div>
                            <p className="text-xs text-content-secondary mt-0.5 line-clamp-2">{notif.message}</p>
                          </div>

                          {/* Actions */}
                          <div className="shrink-0 opacity-0 group-hover:opacity-100 transition flex items-center">
                            {route && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!notif.readAt) markRead(notif.uuid);
                                  navigate(route);
                                  setShowNotifications(false);
                                }}
                                className="p-1 text-content-quaternary hover:text-brand-primary rounded-sm transition"
                                title="Go to"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {!notif.readAt && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markRead(notif.uuid);
                                }}
                                className="p-1 text-content-quaternary hover:text-content-primary rounded-sm transition"
                                title="Mark as read"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notif.uuid);
                              }}
                              className="p-1 text-content-quaternary hover:text-red-400 rounded-sm transition"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="border-t border-border-secondary p-2">
                    <button
                      onClick={() => {
                        navigate("/notifications");
                        setShowNotifications(false);
                      }}
                      className="w-full text-center px-3 py-2 text-sm text-brand-primary hover:bg-interactive-hover rounded-md transition"
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-3 px-3 py-2 hover:bg-surface-secondary rounded-lg transition"
          >
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-medium text-content-primary">{user.displayName}</span>
              <span className="text-xs text-content-tertiary">
                {user.role === "install_admin" ? "Install Admin" : user.isPlatformAdmin ? "Platform Admin" : "Member"}
              </span>
            </div>
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={user.displayName} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 bg-brand-gradient-br rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {user.displayName?.charAt(0) || user.email?.charAt(0) || "?"}
                </span>
              </div>
            )}
          </button>

          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                role="presentation" // NOSONAR — decorative backdrop element
                onClick={() => setShowUserMenu(false)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowUserMenu(false);
                }}
              />
              <div className="absolute top-full right-0 mt-2 w-56 bg-surface-elevated border border-border-secondary rounded-lg shadow-xl z-20 overflow-hidden">
                <div className="p-2">
                  <button
                    onClick={() => {
                      navigate("/settings");
                      setShowUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-content-secondary hover:bg-interactive-hover rounded-md transition"
                  >
                    <UserIcon className="w-4 h-4" />
                    <span>Profile & Settings</span>
                  </button>
                </div>
                <div className="border-t border-border-secondary p-2">
                  <button
                    onClick={() => logout()}
                    className="w-full flex items-center gap-3 px-3 py-2 text-red-400 hover:bg-interactive-hover rounded-md transition"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
